import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Paper, SvgIcon, Tooltip} from "@mui/material";
import {Add, EditOutlined, OpenInNew} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import Button from "../../../../../../components/base/button/button";
import SearchInput from "../../../../../../components/base/input/search_input";
import IconButton from "../../../../../../components/base/button/icon_button";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import CreateDynamicPageDialog from "../../../../../../components/dialogs/create-dynamic-page";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import Api from "../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../core/services/date-utils";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {DynamicPagesListDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../packages/data-grid";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";

const dataGridSaveConfig = {
    name: 'DynamicPagesListViewDataGrid',
    version: 1,
};

const DynamicPagesListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'user-platform.dynamic-pages.list'});
    const history = useHistory();
    const [pages, setPages] = useState([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('dynamic-pages-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canManage = operatorAccessLevels.packages.clientPlatform.manageDynamicPages;
    const canExportPages = operatorAccessLevels.packages.clientPlatform.viewDynamicPages
        && operatorAccessLevels.packages.clientPlatform.exportDynamicPages;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformDynamicPagesList);
        configureBehavioralTool((tags) => tags.userPlatformDynamicPagesList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(canManage ? [{
                name: DynamicPagesListDataGridColumnNames.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                width: 0,
            }] : []),
            {
                name: DynamicPagesListDataGridColumnNames.open,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                width: 0,
            },
            {
                title: t('columns.title'),
                name: DynamicPagesListDataGridColumnNames.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: t('columns.route'),
                name: DynamicPagesListDataGridColumnNames.route,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: t('columns.isPublic'),
                name: DynamicPagesListDataGridColumnNames.isPublic,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.boolean,
                sortable: false,
                width: 150,
            },
        ], [canManage, t]);

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
    } : DefaultPaginationInfo, [dataGridPageSize]);

    /**
     * Fetches dynamic pages and applies client-side keyword filtering and pagination.
     *
     * @type {(function(UseSearchFunctionArg): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const response = await Api.listDynamicPages(args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (!response?.resultFlag)
            return;

        const keyword = args.filters?.keyword?.toString()?.trim()?.toLowerCase() ?? '';
        let items = (response.data ?? []).map(mapDynamicPageListItemFromApi);
        if (keyword) {
            items = items.filter((page) =>
                page.title?.toLowerCase().includes(keyword) ||
                page.route?.toLowerCase().includes(keyword),
            );
        }

        const currentPage = args.paginationInfo?.currentPage ?? 1;
        const pageSize = args.paginationInfo?.pageSize ?? DefaultPaginationInfo.pageSize;
        const startIndex = (currentPage - 1) * pageSize;

        setPages(items.slice(startIndex, startIndex + pageSize));
        args.setPaginationInfo(prevState => ({
            ...prevState,
            length: items.length,
        }));
    }, [isMounted]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings);
    const {setFilters} = searchData;

    /**
     * Applies the entered keyword as a search filter and triggers a new list request.
     *
     * @param {string} keyword
     */
    const searchByKeyword = useCallback((keyword) => {
        setFilters({
            keyword: keyword?.toString()?.trim(),
        });
    }, [setFilters]);

    /**
     * Navigates the user to the single dynamic page view.
     *
     * @param {{ id: number }} page
     */
    const navigateToPage = useCallback((page) => {
        history.push(routeFunctions.userPlatform.dynamicPages.single(page.id));
    }, [history]);

    /**
     * Removes the given dynamic page from the server.
     * On success, refreshes the list; on failure, clears the row removing state.
     *
     * @param {{ id: number }} page
     * @return {Promise<void>}
     */
    const removeDynamicPage = useCallback(async (page) => {
        setPages(prevState => prevState.map(e => e.id === page.id ? {...e, removing: true} : e));
        const response = await Api.removeDynamicPage(page.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            searchData.performSearch?.();
        } else {
            setPages(prevState => prevState.map(e => e.id === page.id ? {...e, removing: false} : e));
        }
    }, [isMounted, searchData]);

    /**
     * Opens a confirmation dialog before removing a dynamic page from the list.
     *
     * @param {{ id: number }} page
     */
    const showRemoveConfirmation = useCallback((page) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-2'}>
                <p className={'text-sm text-color-1 mb-0'}>
                    {t('removeConfirmation')}
                </p>
            </div>,
            {},
            (decision) => decision && removeDynamicPage(page),
        );
    }, [removeDynamicPage, t]);

    /**
     * Handles a successfully created dynamic page:
     * - Refreshes the list
     * - Navigates the user to the new page's single view
     *
     * @param {{ id: number }} page
     */
    const addCreatedPage = useCallback((page) => {
        searchData.performSearch?.();
        navigateToPage(page);
    }, [navigateToPage, searchData]);

    /**
     * Exports visible dynamic page rows to CSV.
     */
    const exportPagesToCsv = useCallback(async () => ({
        title: `${t('exportTitle')} - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
        body: pages.map((page) => ({
            [t('columns.title')]: page.title ?? '',
            [t('columns.route')]: page.route ?? '',
            [t('columns.isPublic')]: page.isPublic ? t('exportYes') : t('exportNo'),
        })),
    }), [pages, t]);

    const rows = useMemo(() => {
        return pages?.map(page => ({
            key: page.id,
            data: page,
            cells: {
                ...(canManage ? {
                    [DynamicPagesListDataGridColumnNames.remove]:
                        <Tooltip title={t('removeTooltip')}>
                            <IconButton
                                onPressed={() => showRemoveConfirmation(page)}
                                color={'paper.error'}
                                className={'remove-icon'}
                                disabled={page.removing}
                            >
                                <SvgIcon component={RemoveIcon} className='icon normal'/>
                            </IconButton>
                        </Tooltip>,
                } : {}),
                [DynamicPagesListDataGridColumnNames.open]:
                    <Tooltip title={canManage ? t('editTooltip') : t('viewTooltip')}>
                        <IconButton
                            color={'secondary'}
                            onPressed={() => navigateToPage(page)}
                        >
                            {
                                canManage
                                    ? <EditOutlined className='icon normal'/>
                                    : <OpenInNew className='icon normal'/>
                            }
                        </IconButton>
                    </Tooltip>,
                [DynamicPagesListDataGridColumnNames.title]: page.title,
                [DynamicPagesListDataGridColumnNames.route]: page.route,
                [DynamicPagesListDataGridColumnNames.isPublic]: page.isPublic,
            },
        })) ?? [];
    }, [canManage, navigateToPage, pages, showRemoveConfirmation, t]);

    return (
        <>
            <div className={'dynamic-pages-list-view w-100'}>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 px-1'}>
                    <h2 className={'text-xl font-weight-semi-bold'}>
                        {t('title')}
                    </h2>
                    {
                        canManage &&
                        <Button
                            className='justify-content-center align-items-center d-flex'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            <Add className='icon small mr-2'/>
                            <p className={'button-label'}>{t('addButton')}</p>
                        </Button>
                    }
                </div>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    initialValue={searchData.filters?.keyword ?? ''}
                    search={searchByKeyword}
                    label={t('keywordLabel')}
                />
                <div className='w-100 my-4'/>
                <Paper className='paper w-100 px-3 pt-2'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns,
                        }}
                        columns={dataGridColumns}
                        rows={rows}
                        getExportInfo={canExportPages ? exportPagesToCsv : undefined}
                        hideFooterActions
                        state={{
                            loading: {state: searchData.loading},
                            pagination: searchData.paginationInfo,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                </Paper>
            </div>
            <CreateDynamicPageDialog
                open={isCreateDialogOpen}
                close={() => setIsCreateDialogOpen(false)}
                onSuccess={addCreatedPage}
            />
        </>
    );
};

export default DynamicPagesListView;

/**
 * Maps a CustomisePage list item from the API to the UI list model.
 *
 * @param {import("../../../../../../../core/services/api/type-declerations/response").CustomisePageListItemApiDs} data
 * @return {import("../../../../../../../core/services/api/type-declerations/response").DynamicPageListItemDs}
 */
const mapDynamicPageListItemFromApi = (data) => ({
    id: data.id,
    title: data.title ?? '',
    route: data.name ?? '',
    isPublic: data.isPublic ?? false,
});
